import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../api.ts'
import { money } from '../format.ts'

interface Spec {
  key: string
  value: string
}

interface Option {
  id: string
  name: string
  price: string | null
  currency: string
  defaultSelected: boolean
  purchasePrice?: string | null
  /** Choice group ("Zbiornik główny", "Sterowanie"); null = add-on equipment. */
  optionGroup?: string | null
  /**
   * A group option that picks ONE model out of a family: the offer prints this
   * model and code, and these parameters replace the family's shared ones.
   */
  variantModel?: string | null
  variantCode?: string | null
  techSpec?: Spec[] | null
}

/**
 * Extras editor — owner only. This is what step 2 of writing an offer ("tick the
 * extras") reads from. A supplier's price list never carries these, so without
 * somewhere to type them, every offer for a real machine would be missing the
 * sprayer's clean-water tank and the ARAG computer.
 *
 * "Domyślne" means the extra is pre-ticked when an offer is created — for the
 * things that practically always go with the machine.
 */
export function OptionsEditor({
  machineId,
  options,
  currency,
  onChanged,
}: {
  machineId: string
  options: Option[]
  currency: string
  onChanged: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function remove(id: string, name: string) {
    if (!confirm(`Usunąć wyposażenie „${name}"?\n\nZniknie z listy przy nowych ofertach. Oferty już wystawione zostaną bez zmian.`)) return
    setError(null)
    try {
      await api.delete(`/api/options/${id}`)
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się usunąć')
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4 mt-4">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="font-semibold text-slate-700 text-sm">Wyposażenie dodatkowe</h2>
          <p className="text-xs text-slate-400">Z tej listy wybiera się dodatki przy wystawianiu oferty.</p>
        </div>
        <button onClick={() => setAdding((v) => !v)} className="text-xs text-brand-700 hover:underline">
          {adding ? 'Anuluj' : '+ dodaj'}
        </button>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-2">{error}</div>}

      {options.length === 0 && !adding && (
        <p className="text-sm text-slate-400">
          Brak. Bez wyposażenia oferta na tę maszynę będzie niepełna — dodaj to, co realnie sprzedajecie razem z nią.
        </p>
      )}

      {options.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
              <th className="pb-1 font-medium">Grupa</th>
              <th className="pb-1 font-medium">Opcja</th>
              <th className="pb-1 font-medium text-right">Cena netto</th>
              <th className="pb-1 font-medium text-right">Koszt własny</th>
              <th className="pb-1 font-medium text-center">Domyślna</th>
              <th className="pb-1" />
            </tr>
          </thead>
          <tbody>
            {options.map((o) =>
              editing === o.id ? (
                <tr key={o.id}>
                  <td colSpan={5} className="py-2">
                    <OptionForm
                      machineId={machineId}
                      option={o}
                      currency={currency}
                      onDone={() => {
                        setEditing(null)
                        onChanged()
                      }}
                      onCancel={() => setEditing(null)}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={o.id} className="border-b border-slate-50 last:border-0 group">
                  <td className="py-1.5 text-xs text-slate-500">{o.optionGroup ?? '—'}</td>
                  <td className="py-1.5">
                    {o.name}
                    {o.variantModel && (
                      <span className="ml-2 text-xs text-slate-400" title="Na ofercie ten model, jego kod i parametry">
                        → {o.variantModel}
                        {o.techSpec?.length ? ` · ${o.techSpec.length} par.` : ''}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular">{money(o.price, o.currency)}</td>
                  <td className="py-1.5 text-right tabular text-slate-500">{money(o.purchasePrice, o.currency)}</td>
                  <td className="py-1.5 text-center">{o.defaultSelected ? '✓' : ''}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(o.id)} className="text-xs text-slate-400 hover:text-brand-700 px-1">
                      zmień
                    </button>
                    <button onClick={() => void remove(o.id, o.name)} className="text-xs text-slate-400 hover:text-red-600 px-1">
                      usuń
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}

      {adding && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <OptionForm
            machineId={machineId}
            currency={currency}
            onDone={() => {
              setAdding(false)
              onChanged()
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}
    </section>
  )
}

function OptionForm({
  machineId,
  option,
  currency,
  onDone,
  onCancel,
}: {
  machineId: string
  option?: Option
  currency: string
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(option?.name ?? '')
  const [price, setPrice] = useState(option?.price ?? '')
  const [purchasePrice, setPurchasePrice] = useState(option?.purchasePrice ?? '')
  const [defaultSelected, setDefaultSelected] = useState(option?.defaultSelected ?? false)
  const [optionGroup, setOptionGroup] = useState(option?.optionGroup ?? '')
  const [variantModel, setVariantModel] = useState(option?.variantModel ?? '')
  const [variantCode, setVariantCode] = useState(option?.variantCode ?? '')
  const [spec, setSpec] = useState<Spec[]>(option?.techSpec?.length ? option.techSpec : [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const body = {
      name,
      price: price === '' ? null : Number(price),
      currency: option?.currency ?? currency,
      defaultSelected,
      // Empty means "we don't track our cost on this extra" — sent as null, not
      // 0, because 0 would tell the margin report the extra is free.
      purchasePrice: purchasePrice === '' ? null : Number(purchasePrice),
      // A group makes this a "pick one" choice on the offer (tank size, control).
      optionGroup: optionGroup.trim() || null,
      // Variant identity only makes sense on a group option; an add-on never
      // renames the machine.
      variantModel: optionGroup.trim() ? variantModel.trim() || null : null,
      variantCode: optionGroup.trim() ? variantCode.trim() || null : null,
      techSpec: optionGroup.trim() ? spec.filter((x) => x.key.trim() || x.value.trim()) : [],
    }
    try {
      if (option) await api.patch(`/api/options/${option.id}`, body)
      else await api.post(`/api/machines/${machineId}/options`, body)
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się zapisać')
      setBusy(false)
    }
  }

  return (
    <form onSubmit={save} className="space-y-2">
      <div className="grid grid-cols-12 gap-2 items-end">
        <label className="col-span-2">
          <span className="block text-xs text-slate-500 mb-0.5">Grupa</span>
          <input
            value={optionGroup}
            onChange={(e) => setOptionGroup(e.target.value)}
            placeholder="np. Sterowanie"
            title="Wypełnij, gdy to WYBÓR (jeden z), np. pojemność zbiornika lub sterowanie. Puste = wyposażenie dodatkowe."
            className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="col-span-3">
          <span className="block text-xs text-slate-500 mb-0.5">Nazwa *</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="np. Zbiornik na czystą wodę 120 l"
            className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="col-span-3">
          <span className="block text-xs text-slate-500 mb-0.5">Cena netto ({currency})</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="col-span-3">
          <span className="block text-xs text-slate-500 mb-0.5">Koszt własny</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.target.value)}
            placeholder="opcjonalny"
            className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="col-span-1 flex items-center gap-1 pb-1" title="Zaznaczone od razu przy nowej ofercie">
          <input type="checkbox" checked={defaultSelected} onChange={(e) => setDefaultSelected(e.target.checked)} />
          <span className="text-xs text-slate-500">dom.</span>
        </label>
      </div>

      {optionGroup.trim() && (
        <div className="rounded border border-slate-100 bg-slate-50 px-3 py-2 space-y-2">
          <p className="text-xs text-slate-500">
            Jeśli ta opcja wybiera <b>konkretny model</b> z rodziny (np. „N 90" w SOLIS N), wpisz co ma trafić na ofertę.
            Parametry o tym samym kluczu zastąpią wspólne parametry maszyny; pozostałe dopiszą się do nich.
          </p>
          <div className="grid grid-cols-12 gap-2 items-end">
            <label className="col-span-6">
              <span className="block text-xs text-slate-500 mb-0.5">Model na ofercie</span>
              <input
                value={variantModel}
                onChange={(e) => setVariantModel(e.target.value)}
                placeholder="np. SOLIS N 90"
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              />
            </label>
            <label className="col-span-6">
              <span className="block text-xs text-slate-500 mb-0.5">Numer katalogowy</span>
              <input
                value={variantCode}
                onChange={(e) => setVariantCode(e.target.value)}
                placeholder="opcjonalny"
                className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>
          <div className="space-y-1">
            {spec.map((x, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={x.key}
                  onChange={(e) => setSpec(spec.map((y, j) => (j === i ? { ...y, key: e.target.value } : y)))}
                  placeholder="np. Moc silnika"
                  className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm"
                />
                <input
                  value={x.value}
                  onChange={(e) => setSpec(spec.map((y, j) => (j === i ? { ...y, value: e.target.value } : y)))}
                  placeholder="np. 91 KM"
                  className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm"
                />
                <button type="button" onClick={() => setSpec(spec.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 px-2" title="Usuń">
                  ×
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setSpec([...spec, { key: '', value: '' }])} className="text-xs text-brand-700 hover:underline">
            + parametr tego modelu
          </button>
        </div>
      )}

      {error && <div className="text-xs text-red-700">{error}</div>}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="text-xs bg-brand-600 text-white px-3 py-1 rounded hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Zapisuję…' : 'Zapisz'}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 px-2 hover:text-slate-700">
          Anuluj
        </button>
      </div>
    </form>
  )
}
